import React, { useState } from 'react';
import { Sprint, SprintAllocation, Task } from '../types';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Plus, X, AlertCircle, Clock, TrendingUp, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { parseDateLike } from '../utils/dateUtils';

interface SprintAllocationManagerProps {
  task: Task;
  sprints: Sprint[];
  onUpdate: (allocations: SprintAllocation[]) => void;
}

export default function SprintAllocationManager({ task, sprints, onUpdate }: SprintAllocationManagerProps) {
  const [allocations, setAllocations] = useState<SprintAllocation[]>(
    task.sprintAllocations || []
  );
  const [selectedSprintId, setSelectedSprintId] = useState<string>('');
  const [hoursToAllocate, setHoursToAllocate] = useState<string>('');

  const formatDate = (value: unknown): string => {
    const parsedDate = parseDateLike(value);
    return parsedDate ? parsedDate.toLocaleDateString() : 'Unknown date';
  };

  // Calculate actual hours per sprint from time logs
  const calculateActualHours = (sprintId: string): number => {
    return task.timeLogs
      .filter(log => log.sprintId === sprintId)
      .reduce((sum, log) => sum + log.hours, 0);
  };

  // Get total allocated hours
  const totalAllocated = allocations.reduce((sum, a) => sum + a.allocatedHours, 0);
  const originalEstimate = task.estimatedHours || 0;
  const remaining = originalEstimate - totalAllocated;

  // Available sprints (not yet allocated)
  const availableSprints = sprints.filter(
    s => !allocations.some(a => a.sprintId === s.id)
  );

  const handleAddAllocation = () => {
    if (!selectedSprintId || !hoursToAllocate) {
      toast.error('Please select a sprint and enter hours');
      return;
    }

    const hours = parseFloat(hoursToAllocate);
    if (hours <= 0) {
      toast.error('Hours must be greater than 0');
      return;
    }

    if (hours > remaining) {
      toast.error(`Only ${remaining.toFixed(1)}h remaining from original estimate`);
      return;
    }

    const sprint = sprints.find(s => s.id === selectedSprintId);
    if (!sprint) return;

    const newAllocation: SprintAllocation = {
      sprintId: sprint.id,
      sprintName: sprint.name,
      allocatedHours: hours,
      actualHours: calculateActualHours(sprint.id),
      status: 'pending',
    };

    const updatedAllocations = [...allocations, newAllocation];
    setAllocations(updatedAllocations);
    onUpdate(updatedAllocations);
    setSelectedSprintId('');
    setHoursToAllocate('');
    toast.success('Sprint allocation added');
  };

  const handleRemoveAllocation = (sprintId: string) => {
    const updatedAllocations = allocations.filter(a => a.sprintId !== sprintId);
    setAllocations(updatedAllocations);
    onUpdate(updatedAllocations);
    toast.success('Sprint allocation removed');
  };

  const handleUpdateStatus = (sprintId: string, status: SprintAllocation['status']) => {
    const updatedAllocations = allocations.map(a =>
      a.sprintId === sprintId ? { ...a, status } : a
    );
    setAllocations(updatedAllocations);
    onUpdate(updatedAllocations);
  };

  // Update actual hours from time logs
  React.useEffect(() => {
    const updatedAllocations = allocations.map(a => ({
      ...a,
      actualHours: calculateActualHours(a.sprintId),
    }));
    
    // Only update if actual hours changed
    const hasChanges = updatedAllocations.some((a, i) => 
      a.actualHours !== allocations[i]?.actualHours
    );
    
    if (hasChanges) {
      setAllocations(updatedAllocations);
    }
  }, [task.timeLogs]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Sprint Allocations</span>
          {originalEstimate > 0 && (
            <Badge variant={remaining >= 0 ? "outline" : "destructive"}>
              {remaining >= 0 ? `${remaining.toFixed(1)}h remaining` : `${Math.abs(remaining).toFixed(1)}h over`}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Original Estimate - Read Only */}
        <div className="bg-primary/10 border border-primary/40 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-semibold text-primary">Original Estimate (Locked)</Label>
              <p className="text-xs text-primary mt-1">This value cannot be changed</p>
            </div>
            <div className="text-3xl font-bold text-primary">
              {originalEstimate}h
            </div>
          </div>
        </div>

        {/* Current Allocations */}
        {allocations.length > 0 && (
          <div className="space-y-3">
            <Label className="text-sm font-semibold">Allocated to Sprints</Label>
            {allocations.map((allocation) => {
              const sprint = sprints.find(s => s.id === allocation.sprintId);
              const actual = allocation.actualHours || 0;
              const allocated = allocation.allocatedHours;
              const variance = actual - allocated;
              const percentComplete = allocated > 0 ? (actual / allocated) * 100 : 0;

              return (
                <div
                  key={allocation.sprintId}
                  className="border rounded-lg p-4 space-y-3 bg-white hover:shadow-md transition-shadow"
                >
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold">{allocation.sprintName}</h4>
                        {sprint && (
                          <Badge variant="outline" className="text-xs">
                            {sprint.status}
                          </Badge>
                        )}
                      </div>
                      {sprint && (
                        <p className="text-xs text-muted-foreground">
                          {formatDate(sprint.startDate)} - {formatDate(sprint.endDate)}
                        </p>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveAllocation(allocation.sprintId)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Hours Breakdown */}
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div className="bg-primary/10 rounded p-2">
                      <div className="text-xs text-primary font-semibold">Allocated</div>
                      <div className="text-lg font-bold text-primary">{allocated}h</div>
                    </div>
                    <div className="bg-chart-2/10 rounded p-2">
                      <div className="text-xs text-chart-2 font-semibold">Actual</div>
                      <div className="text-lg font-bold text-chart-2">{actual.toFixed(1)}h</div>
                    </div>
                    <div className={`rounded p-2 ${variance > 0 ? 'bg-destructive/10' : 'bg-muted/40'}`}>
                      <div className={`text-xs font-semibold ${variance > 0 ? 'text-destructive' : 'text-muted-foreground'}`}>
                        Variance
                      </div>
                      <div className={`text-lg font-bold ${variance > 0 ? 'text-destructive' : 'text-muted-foreground'}`}>
                        {variance > 0 ? '+' : ''}{variance.toFixed(1)}h
                      </div>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div>
                    <div className="flex justify-between text-xs text-muted-foreground mb-1">
                      <span>Progress</span>
                      <span>{percentComplete.toFixed(0)}%</span>
                    </div>
                    <Progress
                      value={Math.min(percentComplete, 100)}
                      className={
                        percentComplete > 100
                          ? 'h-2 [&::-webkit-progress-value]:bg-destructive/10 [&::-moz-progress-bar]:bg-destructive/10'
                          : 'h-2'
                      }
                    />
                  </div>

                  {/* Status */}
                  <div className="flex items-center gap-2">
                    <Label className="text-xs">Status:</Label>
                    <Select
                      value={allocation.status || 'pending'}
                      onValueChange={(value) => handleUpdateStatus(allocation.sprintId, value as any)}
                    >
                      <SelectTrigger className="h-8 w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="in-progress">In Progress</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Warnings */}
                  {variance > 0 && (
                    <div className="flex items-start gap-2 text-xs text-destructive bg-destructive/10 p-2 rounded">
                      <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <span>Over allocated by {variance.toFixed(1)}h</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Add New Allocation */}
        {availableSprints.length > 0 && (
          <div className="space-y-3 border-t pt-4">
            <Label className="text-sm font-semibold">Add to Sprint</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Select Sprint</Label>
                <Select value={selectedSprintId} onValueChange={setSelectedSprintId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose sprint..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableSprints.map((sprint) => (
                      <SelectItem key={sprint.id} value={sprint.id}>
                        {sprint.name} ({sprint.status})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Hours to Allocate</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.5"
                  value={hoursToAllocate}
                  onChange={(e) => setHoursToAllocate(e.target.value)}
                  placeholder="e.g., 8"
                />
              </div>
            </div>
            <Button
              onClick={handleAddAllocation}
              disabled={!selectedSprintId || !hoursToAllocate || remaining <= 0}
              className="w-full"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add to Sprint
            </Button>
          </div>
        )}

        {/* Summary */}
        <div className="border-t pt-4 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Original Estimate:</span>
            <span className="font-semibold">{originalEstimate}h</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Total Allocated:</span>
            <span className={`font-semibold ${totalAllocated > originalEstimate ? 'text-destructive' : ''}`}>
              {totalAllocated.toFixed(1)}h
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Total Actual:</span>
            <span className="font-semibold">
              {task.timeLogs.reduce((sum, log) => sum + log.hours, 0).toFixed(1)}h
            </span>
          </div>
        </div>

        {/* No sprints available */}
        {availableSprints.length === 0 && allocations.length === 0 && (
          <div className="text-center py-8 text-muted-foreground text-sm">
            <Clock className="w-12 h-12 mx-auto mb-2 text-muted-foreground" />
            <p>No sprints available for this project</p>
          </div>
        )}

        {availableSprints.length === 0 && allocations.length > 0 && (
          <div className="text-center py-4 text-sm text-chart-2 bg-chart-2/10 rounded-lg">
            <CheckCircle className="w-6 h-6 mx-auto mb-1" />
            <p>Task is allocated to all available sprints</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
